"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleSheetsSyncSettingsSchema = exports.housingSyncSettingsSchema = exports.smtpSettingsSchema = exports.oidcSettingsSchema = exports.emrIntegrationSettingsSchema = exports.notificationSettingsSchema = exports.productivityDefaultsSchema = exports.schedulingDefaultsSchema = exports.clinicInfoSchema = void 0;
const zod_1 = require("zod");
exports.clinicInfoSchema = zod_1.z.object({
    clinicName: zod_1.z.string().min(1),
    clinicAddress: zod_1.z.string().default(''),
    clinicPhone: zod_1.z.string().default(''),
    crisisLine: zod_1.z.string().default('988'),
    timezone: zod_1.z.string().default('America/New_York'),
    weekStartsOn: zod_1.z.enum(['MON', 'SUN']).default('MON'),
    // EMR deep-link template. `{externalRef}` is replaced with the client's external EMR ID.
    // Example: https://emr.example.com/clients/{externalRef}
    emrClientUrlTemplate: zod_1.z.string().default(''),
});
exports.schedulingDefaultsSchema = zod_1.z.object({
    instanceHorizonWeeks: zod_1.z.number().int().min(1).max(12).default(4),
    providerUtilizationSoftCapPct: zod_1.z.number().min(50).max(100).default(90),
    enableOverrides: zod_1.z.boolean().default(true),
    defaultGroupMaxClients: zod_1.z.number().int().positive().default(24),
});
exports.productivityDefaultsSchema = zod_1.z.object({
    therapistDirectHoursPerWeek: zod_1.z.number().int().default(28),
    prescriberDirectHoursPerWeek: zod_1.z.number().int().default(28),
    nurseDirectHoursPerWeek: zod_1.z.number().int().default(30),
    communityCmDirectHoursPerWeek: zod_1.z.number().int().default(28),
});
// Notification + Twilio SMS configuration. When `enabled=false` (or any required
// Twilio field is blank) the SMS service runs in dry-run mode: it still
// computes who would be notified and writes Notification audit rows with
// status=DRY_RUN, but does not call out to Twilio. This lets dev/demo environments
// exercise the full pipeline without secrets or real SMS costs.
exports.notificationSettingsSchema = zod_1.z.object({
    smsEnabled: zod_1.z.boolean().default(false),
    twilioAccountSid: zod_1.z.string().default(''),
    twilioAuthToken: zod_1.z.string().default(''),
    twilioFromNumber: zod_1.z.string().default(''),
    // Free-text template; `{client}`, `{service}`, `{time}`, `{date}` are substituted at send time.
    missedAppointmentTemplate: zod_1.z
        .string()
        .default('Talbot Behavioral Health: {client} missed {service} at {time} on {date}. Please follow up.'),
});
// EMR integration — outbound HTTP. The CensusScheduler API uses these to call
// the EMR for things like client backfill, candidate push-back, or live
// availability sync. Same redact-on-read pattern as notification_settings:
// the apiKey is masked for non-admins, and a __REDACTED__ round-trip is
// rejected at write time so a STAFF save can't wipe the real key.
//
// `authHeaderName` lets the operator pick the header the EMR expects:
//   'X-API-Key'     → header value is the raw apiKey
//   'Authorization' → header value is `Bearer ${apiKey}`
//   'X-Api-Token'   → same as X-API-Key with a different name
// Add new headers without code changes by entering the literal header name.
exports.emrIntegrationSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    baseUrl: zod_1.z.string().default(''),
    apiKey: zod_1.z.string().default(''),
    authHeaderName: zod_1.z.string().default('Authorization'),
    // When true, send the key as `Bearer ${apiKey}`. When false, send raw apiKey.
    useBearerScheme: zod_1.z.boolean().default(true),
    // Optional EMR tenant / clinic id required by some EMR APIs.
    emrTenantId: zod_1.z.string().default(''),
    // Health-check endpoint relative to baseUrl. Defaults to '/health' which is
    // a common convention; the operator can override with a real ping route.
    healthCheckPath: zod_1.z.string().default('/health'),
    // Hard timeout for outbound HTTP, in seconds.
    timeoutSec: zod_1.z.number().int().min(1).max(120).default(15),
});
// OIDC SSO. Wires the API into an external identity provider (Microsoft Entra ID
// for Office 365, Google, Okta, Auth0, anything OIDC-discoverable) so staff can
// sign in with their org account instead of a CensusScheduler local password.
//
// `issuer` is the IdP base URL. For MS Entra it's
//   https://login.microsoftonline.com/<tenant-id>/v2.0
// `clientId` and `clientSecret` come from the IdP's app registration.
// `redirectUri` is what the IdP redirects to after login — must match what's
// registered with the IdP (e.g., http://localhost:5173/auth/oidc/callback for dev).
// `defaultRole` is the role assigned to first-time SSO users that don't already
// have a User row.
exports.oidcSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    issuer: zod_1.z.string().default(''),
    clientId: zod_1.z.string().default(''),
    clientSecret: zod_1.z.string().default(''),
    redirectUri: zod_1.z.string().default('http://localhost:5173/auth/oidc/callback'),
    scopes: zod_1.z.string().default('openid profile email'),
    // Provider button label, e.g. "Sign in with Microsoft" / "Sign in with Google".
    buttonLabel: zod_1.z.string().default('Sign in with SSO'),
    // First-time SSO users get this role. ADMIN must promote them manually if needed.
    defaultRole: zod_1.z.enum(['ADMIN', 'PROVIDER', 'STAFF']).default('PROVIDER'),
    // Comma-separated list of email domains allowed to sign in. Empty = allow all.
    allowedEmailDomains: zod_1.z.string().default(''),
});
// SMTP / outbound email. Used for: (a) the SMTP "Send test" button on the
// Settings page and (b) the Scheduled Reports runner, which renders a CSV +
// HTML body and emails it to a configured recipient list.
//
// `enabled=false` (or any required field blank) puts the EmailService in
// dry-run mode: it still writes a Notification audit row (`kind=EMAIL`,
// status=DRY_RUN) so you can rehearse a scheduled report without a real
// mail server.
//
// Common setups:
//   Office 365:  host=smtp.office365.com  port=587  secure=false (STARTTLS)
//   Gmail:       host=smtp.gmail.com      port=465  secure=true  (use an app password)
//   SendGrid:    host=smtp.sendgrid.net   port=587  secure=false
//   AWS SES:     host=email-smtp.us-east-1.amazonaws.com  port=587  secure=false
exports.smtpSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    host: zod_1.z.string().default(''),
    port: zod_1.z.number().int().min(1).max(65535).default(587),
    secure: zod_1.z.boolean().default(false), // true for port 465; false for STARTTLS on 587
    username: zod_1.z.string().default(''),
    password: zod_1.z.string().default(''),
    fromAddress: zod_1.z.string().default(''),
    fromName: zod_1.z.string().default('Census Scheduler'),
    // Optional comma-separated reply-to. Falls back to fromAddress when blank.
    replyTo: zod_1.z.string().default(''),
});
// ─── Housing Sync (Microsoft Graph → bedboard) ────────────────────────────
// Pulls per-client housing data from a shared Excel workbook (one tab per
// physical house, one row per resident) on a nightly cron, and pushes the
// clinic's cohort/program assignment back to that workbook so the house
// manager's view stays in sync with the scheduler.
//
// Same redact-on-read pattern as SMTP/OIDC/EMR: `clientSecret` is masked for
// non-admins and the __REDACTED__ round-trip protection in SettingsService
// prevents a non-admin save from wiping the real value.
//
// Setup walkthrough is in the operator docs. The values populated here are:
//   tenantId         — your Azure AD tenant GUID
//   clientId         — Azure app registration's Application (client) ID
//   clientSecret     — Azure app registration secret value (REDACTED on read)
//   sheetUrl         — verbatim SharePoint share URL of the workbook
//   resolvedDriveId  — filled in by "Test connection"; the Graph driveId
//   resolvedItemId   — filled in by "Test connection"; the Graph itemId
//   workbookName     — informational display string
//   cronHour         — 0-23, in clinic-local timezone
//   writebackProgram        — when true, push our cohort→program label to the sheet
//   writebackTalbotId       — when true, stamp cohortId into a "Talbot ID" column
//   autoCreateIntakeCandidate — when true, unmatched new rows → IntakeCandidate
//   lastSyncAt/lastSyncStatus/lastSyncError — denormalized status for UI display
exports.housingSyncSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    tenantId: zod_1.z.string().default(''),
    clientId: zod_1.z.string().default(''),
    clientSecret: zod_1.z.string().default(''),
    sheetUrl: zod_1.z.string().default(''),
    resolvedDriveId: zod_1.z.string().default(''),
    resolvedItemId: zod_1.z.string().default(''),
    workbookName: zod_1.z.string().default(''),
    cronHour: zod_1.z.number().int().min(0).max(23).default(2),
    // ⚠ Writebacks modify the PRODUCTION bedboard.
    //
    // - writebackProgram (default ON):
    //     For LINKED rows ONLY, pushes the client's cohort master-library
    //     name into the bedboard's Program cell on every sync. Cohort is
    //     authoritative; any operator-side edit on the sheet gets reverted
    //     on next sync. Never writes for unlinked/pending rows. To make the
    //     bedboard label shorter, rename the cohort in Master Library —
    //     that name propagates to every linked row.
    //
    // - writebackTalbotId (default OFF):
    //     When enabled, writes the cohortId into the bedboard's Talbot ID
    //     column for LINKED rows — but ONLY when the cell is currently
    //     blank. Never overwrites operator data.
    //
    // Neither writeback ever deletes data — only patches single cells.
    writebackProgram: zod_1.z.boolean().default(true),
    writebackTalbotId: zod_1.z.boolean().default(false),
    autoCreateIntakeCandidate: zod_1.z.boolean().default(false),
    // Denormalized status display (server writes; UI reads).
    lastSyncAt: zod_1.z.string().nullable().default(null),
    lastSyncStatus: zod_1.z.enum(['OK', 'FAILED', 'PARTIAL']).nullable().default(null),
    lastSyncError: zod_1.z.string().nullable().default(null),
    // Tab names discovered by Test connection. Read-only display.
    detectedTabs: zod_1.z.array(zod_1.z.string()).default([]),
});
// ─── Housing Sync (Google Sheets) ──────────────────────────────────────────
// Parallel to housing_sync_settings but for the Google Sheets data source.
// Credentials are a service-account email + PEM private key instead of Azure
// app creds. `serviceAccountKey` is the masked secret (see REDACTED_FIELDS).
// `detectedTabs` is filled by Test connection (the Google analog of
// resolvedDriveId/resolvedItemId). Same writeback + cron + status semantics.
exports.googleSheetsSyncSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    serviceAccountEmail: zod_1.z.string().default(''),
    serviceAccountKey: zod_1.z.string().default(''),
    sheetUrl: zod_1.z.string().default(''),
    cronHour: zod_1.z.number().int().min(0).max(23).default(2),
    // Same writeback contract as housing_sync_settings (see notes there).
    writebackProgram: zod_1.z.boolean().default(true),
    writebackTalbotId: zod_1.z.boolean().default(false),
    autoCreateIntakeCandidate: zod_1.z.boolean().default(false),
    // Denormalized status display (server writes; UI reads).
    lastSyncAt: zod_1.z.string().nullable().default(null),
    lastSyncStatus: zod_1.z.enum(['OK', 'FAILED', 'PARTIAL']).nullable().default(null),
    lastSyncError: zod_1.z.string().nullable().default(null),
    // Tab names discovered by Test connection. Read-only display.
    detectedTabs: zod_1.z.array(zod_1.z.string()).default([]),
});
//# sourceMappingURL=settings.js.map